import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import z from "zod";
import { CreateChannelDialog } from "@/components/forms/channels/create";
import { Button } from "@/components/ui/button";
import {
	DataTableGeneric,
	type DataTableSearchParams,
	DataTableSearchSchema,
} from "@/components/ui/data-table-generic";
import { Input } from "@/components/ui/input";
import { orpc } from "@/orpc/client";
import { columns } from "@/tables/user-channels";

export const Route = createFileRoute("/_app/users/$userId/channels")({
	component: Page,
	params: z.object({
		userId: z.coerce.number(),
	}),
	validateSearch: (search) => DataTableSearchSchema.parse(search),
	async loader({ context, params }) {
		context.queryClient.prefetchQuery(
			orpc.users.listChannels.queryOptions({
				input: {
					userId: params.userId,
				},
			}),
		);
	},
});

function Page() {
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const params = Route.useParams();

	const { data: user } = useSuspenseQuery(
		orpc.users.show.queryOptions({
			input: {
				userId: params.userId,
			},
		}),
	);

	const {
		data: channels,
		isFetching,
		refetch,
	} = useQuery(
		orpc.users.listChannels.queryOptions({
			input: {
				userId: params.userId,
			},
		}),
	);

	const [globalFilter, setGlobalFilter] = useState(search.q ?? "");

	const handleNavigate = useCallback(
		(params: DataTableSearchParams) => {
			navigate({
				replace: true,
				search: (prev) => ({
					...prev,
					...params,
				}),
			});
		},
		[navigate],
	);

	const toolbar = (
		<>
			<div className="relative">
				<Input
					value={globalFilter}
					onChange={(e) => setGlobalFilter(e.target.value)}
					placeholder="Search Channels..."
					className="pl-10"
				/>
				<div className="pointer-events-none absolute left-2 top-0 h-full flex items-center justify-center">
					<IconSearch className="h-4" />
				</div>
			</div>
			<CreateChannelDialog publisherId={user.Publisher.ID} ownerId={user.ID}>
				<Button>
					<IconPlus />
					Create a Channel
				</Button>
			</CreateChannelDialog>
		</>
	);

	return (
		<DataTableGeneric
			columns={columns}
			data={channels ?? []}
			isFetching={isFetching}
			handleReload={() => refetch()}
			toolbar={toolbar}
			searchParams={{
				...search,
				q: globalFilter,
			}}
			onNavigate={handleNavigate}
			getRowId={(row) => row.ID.toString()}
			config={{
				defaultPageSize: 25,
				pageSizeOptions: [25, 50, 100, 500],
			}}
		/>
	);
}
